import React, { useState, useEffect } from 'react';
import { Upload, Trash2, Workflow, RefreshCw } from 'lucide-react';
import { workflowApi } from '../../services/workflowApi';

interface ComfyUIWorkflow {
  id: string;
  name: string;
  supportedModes: string[];
  createdAt: string;
}

export const ComfyUITab: React.FC = () => {
  const [comfyuiWorkflows, setComfyuiWorkflows] = useState<ComfyUIWorkflow[]>([]);
  const [workflowName, setWorkflowName] = useState('');
  const [workflowFile, setWorkflowFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const loadComfyUIWorkflows = async () => {
    try {
      const workflows = await workflowApi.listWorkflows();
      setComfyuiWorkflows(workflows);
    } catch (e) {
      console.error('Failed to load workflows:', e);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setWorkflowFile(file);
      setWorkflowName(file.name.replace('.json', ''));
    }
  };

  const handleUploadWorkflow = async () => {
    if (!workflowFile) {
      setUploadError('请选择工作流文件');
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('file', workflowFile);
      if (workflowName) {
        formData.append('name', workflowName);
      }

      await workflowApi.uploadWorkflow(formData);
      setWorkflowFile(null);
      setWorkflowName('');
      loadComfyUIWorkflows();

      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (e: any) {
      setUploadError(e.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteWorkflow = async (workflowId: string) => {
    try {
      await workflowApi.deleteWorkflow(workflowId);
      loadComfyUIWorkflows();
    } catch (e) {
      console.error('Failed to delete workflow:', e);
    }
  };

  useEffect(() => {
    loadComfyUIWorkflows();
  }, []);

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">上传 ComfyUI 工作流</label>
          <button
            onClick={loadComfyUIWorkflows}
            className="p-1.5 text-slate-500 hover:text-white transition-colors"
          >
            <RefreshCw size={14} />
          </button>
        </div>

        <div className="bg-black/30 border border-white/10 rounded-xl p-4 space-y-4">
          <div className="flex items-center gap-4">
            <label className="flex-1 cursor-pointer">
              <input
                type="file"
                accept=".json"
                className="hidden"
                onChange={handleFileChange}
              />
              <div className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl border transition-colors ${workflowFile ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-400' : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10 hover:text-white'}`}>
                <Upload size={16} />
                <span className="text-xs font-medium">{workflowFile ? workflowFile.name : '选择 JSON 文件'}</span>
              </div>
            </label>
          </div>

          <div className="flex items-center gap-4">
            <input
              type="text"
              className="flex-1 bg-black/30 border border-white/10 rounded-xl py-2 px-3 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-cyan-500/50 transition-colors"
              placeholder="模型名称 (如: My Custom Model)"
              value={workflowName}
              onChange={(e) => setWorkflowName(e.target.value)}
            />
            <button
              onClick={handleUploadWorkflow}
              disabled={isUploading || !workflowFile}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${isUploading || !workflowFile ? 'bg-white/5 text-slate-500 cursor-not-allowed' : 'bg-cyan-500 text-black hover:bg-cyan-400'}`}
            >
              {isUploading ? '上传中...' : '上传'}
            </button>
          </div>

          {uploadError && (
            <p className="text-xs text-red-400">{uploadError}</p>
          )}
        </div>

        <p className="text-[11px] text-slate-500 leading-relaxed">
          上传 ComfyUI 工作流 JSON 文件后，系统会自动推断支持的模式（文生图/图生图/重绘），并将其注册为可用模型。上传成功后刷新页面即可在模型列表中看到。
        </p>
      </div>

      <div className="space-y-4 pt-4 border-t border-white/5">
        <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">已注册的工作流</label>

        {comfyuiWorkflows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-slate-500">
            <Workflow size={32} className="mb-2 opacity-50" />
            <span className="text-xs">暂无工作流</span>
          </div>
        ) : (
          <div className="space-y-2">
            {comfyuiWorkflows.map(wf => (
              <div
                key={wf.id}
                className="flex items-center justify-between p-3 bg-black/30 border border-white/10 rounded-xl"
              >
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-white">{wf.name}</span>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[10px] text-slate-500 font-mono">ID: {wf.id}</span>
                    <span className="text-[10px] text-cyan-400/70">
                      {wf.supportedModes.join(', ')}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteWorkflow(wf.id)}
                  className="p-2 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
